<script>
  import { takeHome } from "$calc/tax.js";
  import { annualRepayment } from "$calc/plans.js";
  import taxYear from "$data/2025-26.json";
  import { graduate } from "./appState.svelte.js";

  const PLANS = [
    { key: "none", label: "No student loan" },
    { key: "plan1", label: "Plan 1 — pre-2012 undergrad / NI" },
    { key: "plan2", label: "Plan 2 — 2012–2023 undergrad (E&W)" },
    { key: "plan3", label: "Plan 3 — Postgraduate" },
    { key: "plan4", label: "Plan 4 — Scotland (SAAS)" },
    { key: "plan5", label: "Plan 5 — 2023+ undergrad (England)" },
  ];

  const LOCATIONS = [
    { key: "englandNIWales", label: "England, Wales or Northern Ireland" },
    { key: "scotland", label: "Scotland" },
  ];

  const PENSION_METHODS = [
    { key: "none", label: "None" },
    { key: "salarySacrifice", label: "Salary sacrifice (best if offered)" },
    { key: "reliefAtSource", label: "Relief at source (basic-rate)" },
  ];

  let locationKey = $state("englandNIWales");
  let pensionMethod = $state("none");
  let pensionPercent = $state(5);

  const period = $state({ value: "monthly" });

  // Derived state
  const band = $derived(taxYear.incomeTax[locationKey]);
  const niData = $derived(taxYear.nationalInsurance.class1Employee);
  const planData = $derived(
    graduate.planKey === "none" ? null : taxYear.studentLoans[graduate.planKey],
  );

  const slcRepaymentFn = $derived(
    planData ? (s) => annualRepayment(s, planData) : () => 0,
  );

  const pensionGrossAnnual = $derived((pensionPercent / 100) * Math.max(0, graduate.salary));
  const salarySacrifice = $derived(
    pensionMethod === "salarySacrifice" ? pensionGrossAnnual : 0,
  );

  const result = $derived(
    takeHome({
      salary: Math.max(0, graduate.salary),
      band,
      niData,
      slcRepayment: slcRepaymentFn,
      pensionSalarySacrifice: salarySacrifice,
    }),
  );

  // Handle relief-at-source pension: contribution comes out of NET pay
  // and provider tops up basic-rate relief. We deduct it AFTER the takeHome
  // calc; higher-rate relief is claimed separately via SA and shown as an
  // annual tax reduction rather than a monthly line (out of scope for v1).
  const rasPensionFromNet = $derived(
    pensionMethod === "reliefAtSource"
      ? pensionGrossAnnual * (1 - taxYear.pension.reliefAtSourceBasicRate)
      : 0,
  );

  const netAfterPension = $derived(result.net - rasPensionFromNet);

  const divisor = $derived(
    period.value === "annual" ? 1 : period.value === "weekly" ? 52 : 12,
  );

  // Marginal £1 uplift: what does an extra £100 gross salary yield net?
  const marginal = $derived.by(() => {
    const bump = 100;
    const bumped = takeHome({
      salary: Math.max(0, graduate.salary) + bump,
      band,
      niData,
      slcRepayment: slcRepaymentFn,
      pensionSalarySacrifice: salarySacrifice,
    });
    return (bumped.net - result.net) / bump;
  });

  const fmtGBP = (n) =>
    new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);

  const fmtGBPShort = (n) =>
    new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      maximumFractionDigits: 0,
    }).format(n);

  const fmtPct = (n) =>
    new Intl.NumberFormat("en-GB", {
      style: "percent",
      maximumFractionDigits: 1,
    }).format(n);
</script>

<section class="screen">
  <div class="intro">
    <h2>Take-home calculator</h2>
    <p>
      What lands in your account after tax, National Insurance,
      student-loan repayment and pension. Most calculators skip the SLC
      line — this one shows it explicitly.
    </p>
  </div>

  <div class="layout">
    <aside class="inputs card">
      <h3>Your salary</h3>

      <label class="field">
        <span class="label">Gross annual salary</span>
        <div class="prefix-input">
          <span class="prefix">£</span>
          <input type="number" min="0" step="500" bind:value={graduate.salary} />
        </div>
        <span class="hint-sync">Shared with Loan modeller</span>
      </label>

      <label class="field">
        <span class="label">Location</span>
        <select bind:value={locationKey}>
          {#each LOCATIONS as loc (loc.key)}
            <option value={loc.key}>{loc.label}</option>
          {/each}
        </select>
      </label>

      <label class="field">
        <span class="label">Student loan plan</span>
        <select bind:value={graduate.planKey}>
          {#each PLANS as p (p.key)}
            <option value={p.key}>{p.label}</option>
          {/each}
        </select>
        <span class="hint-sync">Shared with Loan modeller</span>
      </label>

      <h3 class="section-heading">Pension</h3>

      <label class="field">
        <span class="label">Method</span>
        <select bind:value={pensionMethod}>
          {#each PENSION_METHODS as pm (pm.key)}
            <option value={pm.key}>{pm.label}</option>
          {/each}
        </select>
      </label>

      {#if pensionMethod !== "none"}
        <label class="field">
          <span class="label">Contribution (% of salary)</span>
          <div class="suffix-input">
            <input
              type="number"
              min="0"
              max="100"
              step="0.5"
              bind:value={pensionPercent}
            />
            <span class="suffix">%</span>
          </div>
        </label>
        <p class="hint">
          {fmtGBPShort(pensionGrossAnnual)} / year going into the pension
        </p>
      {/if}
    </aside>

    <section class="output">
      <div class="headline card">
        <div class="outcome-label">Take-home</div>
        <div class="period-toggle">
          {#each ["annual", "monthly", "weekly"] as p (p)}
            <button
              type="button"
              class="period-btn"
              class:active={period.value === p}
              onclick={() => (period.value = p)}
            >
              {p}
            </button>
          {/each}
        </div>
        <div class="big-figure">{fmtGBP(netAfterPension / divisor)}</div>
        <div class="secondary-figures">
          <span>{fmtGBPShort(graduate.salary / divisor)} gross</span>
          <span class="dot">·</span>
          <span>{fmtPct(netAfterPension / graduate.salary)} of gross retained</span>
        </div>
      </div>

      <div class="breakdown card">
        <h3>Breakdown</h3>
        <table>
          <tbody>
            <tr class="row-gross">
              <td>Gross salary</td>
              <td class="num">{fmtGBP(graduate.salary / divisor)}</td>
            </tr>

            {#if salarySacrifice > 0}
              <tr class="row-deduction">
                <td>
                  <span class="dash">−</span> Pension
                  <span class="tag tag-sacrifice">salary sacrifice</span>
                </td>
                <td class="num">{fmtGBP(salarySacrifice / divisor)}</td>
              </tr>
              <tr class="row-subtotal">
                <td>Taxable salary</td>
                <td class="num">{fmtGBP(result.taxableSalary / divisor)}</td>
              </tr>
            {/if}

            <tr class="row-deduction">
              <td><span class="dash">−</span> Income tax</td>
              <td class="num">{fmtGBP(result.tax / divisor)}</td>
            </tr>
            <tr class="row-deduction">
              <td><span class="dash">−</span> National Insurance</td>
              <td class="num">{fmtGBP(result.ni / divisor)}</td>
            </tr>
            {#if result.slc > 0}
              <tr class="row-deduction row-slc">
                <td>
                  <span class="dash">−</span> Student loan
                  <span class="tag tag-slc">{PLANS.find(p => p.key === graduate.planKey).label.split(' — ')[0]}</span>
                </td>
                <td class="num">{fmtGBP(result.slc / divisor)}</td>
              </tr>
            {/if}

            {#if rasPensionFromNet > 0}
              <tr class="row-deduction">
                <td>
                  <span class="dash">−</span> Pension
                  <span class="tag">from net; provider claims basic-rate relief</span>
                </td>
                <td class="num">{fmtGBP(rasPensionFromNet / divisor)}</td>
              </tr>
            {/if}

            <tr class="row-net">
              <td><strong>Take-home</strong></td>
              <td class="num"><strong>{fmtGBP(netAfterPension / divisor)}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="quick-stats">
        <div class="stat">
          <span class="stat-label">Marginal £ retained</span>
          <span class="stat-value">{fmtPct(marginal)}</span>
          <span class="stat-hint">on your next £1 of gross salary</span>
        </div>
        <div class="stat">
          <span class="stat-label">Total deducted</span>
          <span class="stat-value">{fmtGBPShort((graduate.salary - netAfterPension))}</span>
          <span class="stat-hint">per year across all lines</span>
        </div>
        <div class="stat">
          <span class="stat-label">Effective rate</span>
          <span class="stat-value">{fmtPct(1 - netAfterPension / graduate.salary)}</span>
          <span class="stat-hint">total deductions ÷ gross</span>
        </div>
      </div>

      {#if pensionMethod === "salarySacrifice"}
        <div class="note card">
          <strong>Salary sacrifice</strong> reduces the salary your employer
          reports to HMRC — you save income tax
          <em>and</em> National Insurance on the sacrificed amount.
          Relief at source only saves income tax.
        </div>
      {:else if pensionMethod === "reliefAtSource" && graduate.salary > taxYear.incomeTax.englandNIWales.bands[1].upTo}
        <div class="note card note-warning">
          You're a higher-rate taxpayer.
          <strong>Relief at source</strong> only gives you the 20% basic-rate
          top-up automatically; the extra 20% higher-rate relief must be
          claimed via Self Assessment or by writing to HMRC. Salary sacrifice
          gives it to you at source and also saves 2% NI. Ask your employer
          if it's an option.
        </div>
      {/if}
    </section>
  </div>
</section>

<style>
  .screen { display: flex; flex-direction: column; gap: 1.25rem; }
  .intro h2 { margin-bottom: 0.35rem; }
  .intro p { color: var(--fg-muted); margin: 0; font-size: 0.95rem; max-width: 62ch; }

  .layout {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
  @media (min-width: 900px) {
    .layout { grid-template-columns: minmax(280px, 320px) 1fr; }
  }

  .card {
    background: var(--card);
    border: 1px solid var(--card-border);
    border-radius: 0.9rem;
    padding: 1.15rem 1.25rem;
    box-shadow: var(--card-shadow);
  }

  .inputs h3 { margin-bottom: 0.9rem; }
  .section-heading {
    margin-top: 1.25rem;
    padding-top: 1rem;
    border-top: 1px solid var(--card-border);
    margin-bottom: 0.9rem;
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    margin-bottom: 0.85rem;
  }
  .label {
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--fg-muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  input[type="number"], select {
    font: inherit;
    font-size: 1rem;
    padding: 0.55rem 0.75rem;
    border: 1px solid var(--card-border);
    border-radius: 0.5rem;
    background: var(--bg);
    color: var(--fg);
    width: 100%;
    transition: border-color 0.15s, background 0.15s;
  }
  input[type="number"]:focus, select:focus {
    outline: none;
    border-color: var(--accent);
    background: var(--card);
  }
  .prefix-input, .suffix-input { position: relative; }
  .prefix-input input { padding-left: 1.75rem; }
  .prefix {
    position: absolute;
    left: 0.75rem; top: 50%;
    transform: translateY(-50%);
    color: var(--fg-muted);
    font-weight: 600;
    pointer-events: none;
  }
  .suffix-input input { padding-right: 1.75rem; }
  .suffix {
    position: absolute;
    right: 0.75rem; top: 50%;
    transform: translateY(-50%);
    color: var(--fg-muted);
    font-weight: 600;
    pointer-events: none;
  }
  .hint {
    color: var(--fg-subtle);
    font-size: 0.78rem;
    margin: -0.35rem 0 0.85rem;
  }
  .hint-sync {
    font-size: 0.7rem;
    color: var(--accent);
    font-weight: 500;
    opacity: 0.75;
    font-style: italic;
  }

  .output { display: flex; flex-direction: column; gap: 1.25rem; }

  .headline {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    padding: 1.5rem;
    background: linear-gradient(135deg, var(--accent) 0%, var(--accent-2) 100%);
    color: var(--accent-fg);
    border: none;
  }
  .outcome-label {
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    opacity: 0.8;
    font-weight: 700;
  }
  .period-toggle {
    display: inline-flex;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 999px;
    padding: 3px;
    align-self: flex-start;
    margin: 0.25rem 0;
  }
  .period-btn {
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.7);
    padding: 0.3rem 0.85rem;
    border-radius: 999px;
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: capitalize;
    transition: background 0.15s, color 0.15s;
  }
  .period-btn.active {
    background: white;
    color: var(--accent);
  }
  .big-figure {
    font-size: 2.5rem;
    font-weight: 800;
    line-height: 1.1;
    font-variant-numeric: tabular-nums;
  }
  .secondary-figures {
    display: flex;
    gap: 0.35rem;
    align-items: center;
    font-size: 0.9rem;
    opacity: 0.9;
  }
  .dot { opacity: 0.5; }

  .breakdown h3 { margin-bottom: 0.75rem; }
  .breakdown table {
    width: 100%;
    border-collapse: collapse;
    font-variant-numeric: tabular-nums;
  }
  .breakdown td {
    padding: 0.55rem 0.35rem;
    border-bottom: 1px solid var(--card-border);
    font-size: 0.95rem;
  }
  .breakdown td:first-child { color: var(--fg); }
  .breakdown td.num { text-align: right; }
  .dash {
    color: var(--fg-subtle);
    font-weight: 700;
    display: inline-block;
    width: 1em;
    text-align: center;
  }
  .row-deduction td { color: var(--fg-muted); }
  .row-deduction td:last-child { color: var(--fg); font-weight: 600; }
  .row-subtotal td {
    color: var(--fg);
    font-weight: 700;
    background: var(--bg-2);
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }
  .row-net td {
    padding-top: 0.85rem;
    padding-bottom: 0.85rem;
    font-size: 1.05rem;
    color: var(--accent);
    border-bottom: none;
    border-top: 2px solid var(--accent);
  }
  .row-gross td {
    padding-top: 0.35rem;
  }

  .tag {
    display: inline-block;
    background: var(--accent-tint);
    color: var(--accent);
    padding: 0.1em 0.5em;
    border-radius: 0.4em;
    font-size: 0.72rem;
    font-weight: 600;
    margin-left: 0.35rem;
    letter-spacing: 0.01em;
    vertical-align: middle;
  }
  .tag-slc { background: rgba(207, 138, 44, 0.15); color: var(--warning); }
  .tag-sacrifice { background: rgba(30, 126, 94, 0.15); color: var(--success); }

  .quick-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.75rem;
  }
  .stat {
    background: var(--card);
    border: 1px solid var(--card-border);
    border-radius: 0.7rem;
    padding: 0.85rem 0.9rem;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    box-shadow: var(--card-shadow);
  }
  .stat-label {
    font-size: 0.7rem;
    color: var(--fg-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 600;
  }
  .stat-value {
    font-size: 1.35rem;
    font-weight: 700;
    color: var(--fg);
    font-variant-numeric: tabular-nums;
  }
  .stat-hint {
    font-size: 0.72rem;
    color: var(--fg-subtle);
  }

  .note {
    background: var(--accent-tint);
    border: 1px solid rgba(15, 61, 62, 0.15);
    color: var(--fg);
    font-size: 0.9rem;
    line-height: 1.5;
    padding: 1rem 1.15rem;
  }
  .note-warning {
    background: rgba(207, 138, 44, 0.1);
    border-color: rgba(207, 138, 44, 0.25);
  }

  @media (max-width: 500px) {
    .quick-stats { grid-template-columns: 1fr; }
    .big-figure { font-size: 2rem; }
    .card { padding: 1rem; }
  }
</style>
